import { useState } from 'react';
import type { Object3D } from 'three';
import { useConstraintsStore } from '@/state/constraintsStore';
import { useConfigStore } from '@/state/configStore';
import { useUiStore } from '@/state/uiStore';
import { useHingesStore } from '@/state/hingesStore';
import { useMetadataStore } from '@/state/metadataStore';
import { generateHouse } from '@/generator/generateHouse';
import { exportToGlb, downloadBlob } from '@/export/exportGlb';
import { exportToStl } from '@/export/exportStl';
import { exportToUsda } from '@/export/exportUsda';
import { buildExportMetadata } from '@/export/metadata';
import { randomSeed } from '@/lib/seed';
import type { GenerationConstraints } from '@/generator/constraints';

interface Props {
  getHouseRoot: () => Object3D | null;
}

/**
 * The single Generate button has two subtly different behaviours folded in:
 *   - "Apply": inputs changed since the last generation → regenerate with
 *     the current seed + new constraints. Deterministic from the user's
 *     point of view ("I changed bedrooms from 2 to 3, show me that house").
 *   - "Re-roll": inputs are identical to what produced the current output
 *     → advance the seed first so the click produces a visibly new house.
 *
 * This removes the previous two-button split where "Generate" with
 * unchanged inputs silently produced the same house and left the user
 * wondering whether anything happened.
 */
function signatureOf(input: GenerationConstraints): string {
  return JSON.stringify(input);
}

export function ActionBar({ getHouseRoot }: Props) {
  const [busy, setBusy] = useState<string | null>(null);

  const onGenerate = () => {
    const store = useConstraintsStore.getState();
    if (!store.seed) {
      store.setSeed(randomSeed());
    }

    // Decide seed handling BEFORE we build the input snapshot.
    const currentSig = signatureOf(store.toGenerationInput());
    const lastSig = useConfigStore.getState().lastSignature;
    let mode: 'apply' | 'reroll';
    if (lastSig !== null && currentSig === lastSig) {
      // Nothing changed — treat the click as "re-roll".
      useConstraintsStore.getState().setSeed(randomSeed());
      mode = 'reroll';
    } else {
      mode = 'apply';
    }

    const input = useConstraintsStore.getState().toGenerationInput();
    const signature = signatureOf(input);
    try {
      const result = generateHouse(input);
      useConfigStore.getState().setResult(result, signature);
      useUiStore
        .getState()
        .pushToast(
          mode === 'reroll'
            ? `Re-rolled · seed ${result.config.seed}`
            : `Generated · score ${result.score.total}`,
          'success',
        );
    } catch (err) {
      useUiStore
        .getState()
        .pushToast(`Generation failed: ${(err as Error).message}`, 'warn');
    }
  };

  const onExportGlb = async () => {
    const root = getHouseRoot();
    if (!root) return;
    setBusy('glb');
    try {
      const cfg = useConfigStore.getState().config;
      const constraints = useConstraintsStore.getState().toGenerationInput();
      const { siteAddress, siteDescription } = useMetadataStore.getState();
      const metadata = cfg
        ? buildExportMetadata({ config: cfg, constraints, siteAddress, siteDescription })
        : undefined;
      const blob = await exportToGlb(root, { metadata });
      downloadBlob(blob, `structure-${cfg?.seed ?? 'house'}.glb`);
    } finally {
      setBusy(null);
    }
  };

  const onExportStl = () => {
    const root = getHouseRoot();
    if (!root) return;
    if (!window.confirm('STL export is experimental. Geometry is not guaranteed to be watertight or fabrication-ready. Continue?')) return;
    setBusy('stl');
    try {
      const blob = exportToStl(root);
      downloadBlob(blob, `structure-${useConfigStore.getState().config?.seed ?? 'house'}.stl`);
    } finally {
      setBusy(null);
    }
  };

  const onExportUsda = () => {
    const root = getHouseRoot();
    if (!root) return;
    setBusy('usda');
    try {
      const cfg = useConfigStore.getState().config;
      const seed = cfg?.seed ?? 'house';
      const constraints = useConstraintsStore.getState().toGenerationInput();
      const { siteAddress, siteDescription } = useMetadataStore.getState();
      const metadata = cfg
        ? buildExportMetadata({ config: cfg, constraints, siteAddress, siteDescription })
        : undefined;
      // Use the live hinge values so the exported file shows doors/windows
      // in their currently-set positions; the def metadata keeps the
      // limits intact so a downstream pipeline can re-animate them.
      const { values: hingeValues, inverted: hingeInverted } = useHingesStore.getState();
      const blob = exportToUsda(root, {
        title: `structure · seed ${seed}`,
        hingeValues,
        hingeInverted,
        metadata,
      });
      downloadBlob(blob, `structure-${seed}.usda`);
    } finally {
      setBusy(null);
    }
  };

  const autoRotate = useUiStore((s) => s.autoRotate);
  const setAutoRotate = useUiStore((s) => s.setAutoRotate);

  return (
    <div className="actionbar panel" role="toolbar" aria-label="Actions">
      <button
        type="button"
        className="primary"
        onClick={onGenerate}
        title="Apply any changed settings, or re-roll with a new seed if nothing changed"
      >
        Generate
      </button>
      <span style={{ opacity: 0.4 }}>|</span>
      <button type="button" onClick={onExportGlb} disabled={busy !== null}>
        {busy === 'glb' ? 'Exporting…' : 'Export GLB'}
      </button>
      <button
        type="button"
        onClick={onExportUsda}
        disabled={busy !== null}
        title="Universal Scene Description (ASCII) — preserves hinges and current open angles"
      >
        {busy === 'usda' ? 'Exporting…' : 'Export USDA'}
      </button>
      <button
        type="button"
        onClick={onExportStl}
        disabled={busy !== null}
        title="Experimental — not fabrication-grade"
      >
        {busy === 'stl' ? 'Exporting…' : 'STL (exp.)'}
      </button>
      <span style={{ opacity: 0.4 }}>|</span>
      <label className="toggle">
        <input
          type="checkbox"
          checked={autoRotate}
          onChange={(e) => setAutoRotate(e.target.checked)}
        />
        Auto-rotate
      </label>
    </div>
  );
}
